import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {Form, Dropdown, Popup, Dimmer, Loader} from 'semantic-ui-react';
import {Grid, GridColumn as Column} from '@progress/kendo-react-grid';
import {toast, SemanticToastContainer} from 'react-semantic-toasts';
import {useDataInspector} from '../../providers/DataInspectorProvider';
import moment from 'moment';
import {EditPointModal} from './Modals/EditPointModal';
import {FunctionDiagnostics} from "./Modals/FunctionDiagnostics";
import './LoadingCircle.css';
import styles from './DataInspector.module.css';
import {showColumns} from "./showColumns";
import {useAuth} from "../../providers/AuthenticationProvider";
const WEB_PAGE = 'Data Inspector';

const dateFormatter = date => {
  const d = new Date(date);
  return d.toLocaleString();
};

export const DataInspector = () => {
  const { checkAuth, getUserType } = useAuth();

  const [groupOptions, setGroupOptions] = useState([]);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [pointOptions, setPointOptions] = useState([]);
  const [functionOptions, setFunctionOptions] = useState([]);

  const [group, setGroup] = useState();
  const [buildingSelected, setBuildingsSelected] = useState();
  const [equipmentSelected, setEquipmentSelected] = useState([]);
  const [totalEquipmentSelected, setTotalEquipmentSelected] = useState([]);
  const [point, setPoint] = useState(null);
  const [functionIds, setFunctionIds] = useState([]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState( new Date().toISOString().split('T')[0]);
  const [disabled, setDisabled] = useState(true);

  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [total, setTotal] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortState, setSortState] = useState([{fieldName: 'normalizedTime', dir: 'desc'}]);
  const [data, setData] = useState(null);
  const [searched, setSearched] = useState(false);
  const [multiLine, setMultiLine] = useState(false);
  const [showStales, setShowStales] = useState(false);

  const [editPointOpen, setEditPointOpen] = useState(false);
  const [editPointData, setEditPointData] = useState(null);
  const [functionDiagnosticsOpen, setFunctionDiagnosticsOpen] = useState(false);
  const [functionDiagnosticsData, setFunctionDiagnosticsData] = useState(null);

  const [columns, setColumns] = useState(showColumns);
  const navigate = useNavigate();

  checkAuth(getUserType(), WEB_PAGE) && navigate('/');

  const columnsDropdown = showColumns.map((column, index) => {
    return {
      key: index,
      text: column.title,
      value: column.title,
    };
  });


  const {
    getDataInspectorGroups,
    getDataInspectorBuildings,
    getDataInspectorEquipment,
    getDataInspectorEquipmentPoints,
    getDataInspectorEquipmentFunctions,
    postDataInspectorData,
    postDataInspectorExportData,
    groups,
    buildings,
    equipment,
    points,
    functions,
  } = useDataInspector();

  const handleExport = () => {
    setPrintLoading(true);
    var submit = {
      StartDateTime: startDate,
      EndDateTime: endDate,
      GroupId: group,
      EquipmentIds: totalEquipmentSelected,
      PointIds: point ?? [],
      FunctionIds: [...new Set(functionIds.map(a => a.functionId))] ?? [],
      FunctionEquipmentPairs: functionIds ?? [],
      BuildingIds : buildingSelected ?? [],
      PageSize: pageSize,
      Page: page,
      Sort: sortState[0].fieldName,
      SortDirection: sortState[0].dir,
      MultiLine: multiLine,
    };

    postDataInspectorExportData(submit).then(
      function (value) {
        setPrintLoading(false);
        toast({
          type: 'success',
          description: 'Export Completed, it is now available on the Reports Page',
          animation: 'fade down',
          time: 5000,
          onClick: () => {
            navigate(`/reports`);
          },
        });
      },
      function (error) {
        if (error.response.status === 400) {
          toast({
            type: 'error',
            description: 'Sorry there is no Customer with that Group',
            animation: 'fade down',
            time: 5000,
          });
          setPrintLoading(false);
        }
      }
    );
  };

  const handleSubmit = useCallback(() => {
    setLoading(true);

    if (startDate && endDate && group && buildingSelected && (point.length > 0 || functionIds.length > 0)) {
      setSearched(true);

      var submit = {
        StartDateTime: moment(startDate).toISOString(),
        EndDateTime: moment(endDate).toISOString(),
        GroupId: group,
        EquipmentIds: totalEquipmentSelected,
        PointIds: point ?? [],
        FunctionIds: [...new Set(functionIds.map(a => a.functionId))] ?? [],
        FunctionEquipmentPairs: functionIds ?? [],
        PageSize: pageSize,
        Page: page,
        Sort: sortState[0].fieldName,
        SortDirection: sortState[0].dir,
      };
      postDataInspectorData(submit).then(
        function (value) {
          let tempdata = [];
          if (point || functionIds) {
            value.objectPointRecords.forEach(function (element) {
              if (element.points[0] !== null || element.functions[0] !== undefined) {
                var pointIndex;
                var functionIndex;

                if (element.points.length > 0) {
                  pointIndex = 0;
                  functionIndex = -1;
                } else {
                  pointIndex = -1;
                  functionIndex = 0;
                }

                const tablerow = {};
                tablerow.objectId = element.objectId;
                tablerow.objectName = element.objectName;
                tablerow.groupId = element.groupId;
                tablerow.normalizedTime = element.normalizedTime;
                tablerow.normalizedTimeFormatted = dateFormatter(new Date(element.normalizedTime));
                tablerow.OAT = element.oat;
                tablerow.dewpt = element.dewpt;
                tablerow.enthalpy = element.enthalpy;
                tablerow.insertTime = dateFormatter(new Date(element.insertTime));
                tablerow.lastUpdateTime = dateFormatter(new Date(element.lastUpdateTime));

                if (element.points.length > 0) {
                  tablerow.point = {};
                  tablerow.point.pointId = element.points[pointIndex].objectPointId || '';
                  tablerow.point.componentPointId = element.points[pointIndex].componentPointId;
                  tablerow.point.timestampFormatted = dateFormatter(
                    new Date(element.points[pointIndex].timestamp)
                  );
                  tablerow.point.timestamp = element.points[pointIndex].timestamp;
                  tablerow.point.value = element.points[pointIndex].value;
                  tablerow.point.pointCode = element.points[pointIndex].pointCode;
                  tablerow.point.stale = element.points[pointIndex].stale ? 'True' : 'False';
                  tablerow.point.down = element.points[pointIndex].down ? 'True' : 'False';
                  tablerow.point.fault = element.points[pointIndex].fault ? 'True' : 'False';
                  tablerow.point.duplicate = element.points[pointIndex].duplicate
                    ? 'True'
                    : 'False';
                  tablerow.point.error = element.points[pointIndex].error ? 'True' : 'False';
                  tablerow.point.errorMessage = element.points[pointIndex].errorMessage
                    ? element.points[pointIndex].errorMessage
                    : 'N/A';
                } else {
                  tablerow.point = {};
                  tablerow.point.pointId = '';
                  tablerow.point.componentPointId = '';
                  tablerow.point.timestampFormatted = '';
                  tablerow.point.timestamp = '';
                  tablerow.point.value = '';
                  tablerow.point.pointCode = '';
                  tablerow.point.stale = '';
                  tablerow.point.down = '';
                  tablerow.point.fault = '';
                  tablerow.point.duplicate = '';
                  tablerow.point.error = '';
                  tablerow.point.errorMessage = '';
                }

                if (functionIndex !== -1 && element.functions[0] !== null) {
                  tablerow.function = {};
                  tablerow.function.functionIds = element.functions[functionIndex].id;
                  tablerow.function.name = element.functions[functionIndex].name;
                  tablerow.function.value = element.functions[functionIndex].value;
                  tablerow.function.error = element.functions[functionIndex].error
                    ? 'True'
                    : 'False';
                  tablerow.function.errorMessage = element.functions[functionIndex].errorMessage
                    ? element.functions[functionIndex].errorMessage
                    : 'N/A';
                } else {
                  tablerow.function = {};
                  tablerow.function.functionIds = '';
                  tablerow.function.name = '';
                  tablerow.function.value = '';
                  tablerow.function.error = '';
                  tablerow.function.errorMessage = '';
                }

                tempdata.push(tablerow);
              }
            });
          }
          setData(tempdata);
          setPageCount(value.pageCount);
          setTotal(value.count);
          setLoading(false);
        },
        function (error) {
          if (error.response.status === 400) {
            toast({
              type: 'error',
              description: 'Sorry there is no Customer with that Group',
              animation: 'fade down',
              time: 5000,
            });
          }
        }
      );
    }
  }, [
    endDate,
    buildingSelected,
    group,
    page,
    pageSize,
    point,
    postDataInspectorData,
    sortState,
    startDate,
    functionIds,
    totalEquipmentSelected,
  ]);

  useEffect(() => {
    if (startDate && endDate && group && buildingSelected && (point.length > 0 || functionIds.length > 0) && searched) {
      if (editPointOpen === false) {
        handleSubmit();
      }
    }
  }, [
    pageSize,
    page,
    sortState,
    startDate,
    endDate,
    group,
    buildingSelected,
    equipmentSelected,
    point,
    functionIds,
    searched,
    handleSubmit,
    editPointOpen,
  ]);

  const handleGroupChange = groupId => {
    setGroup(groupId);
    getDataInspectorBuildings(groupId, showStales);

    setBuildingsSelected([]);
    setEquipmentSelected([]);
    setPoint([]);
    setFunctionIds([]);

    setBuildingOptions([]);
    setEquipmentOptions([]);
    setPointOptions([]);
    setFunctionOptions([]);
  };

  const handleBuildingChange = buildingIds => {
    setBuildingsSelected(buildingIds);

    if(buildingIds.length > 0) {
      getDataInspectorEquipment({
        buildingIds: buildingIds ?? [],
        showStale: showStales
      });
      getDataInspectorEquipmentPoints({
        buildingIds: buildingIds ?? [],
        equipmentIds: equipmentSelected ?? [],
        showStale: showStales
      });
      getDataInspectorEquipmentFunctions({
        buildingIds: buildingIds ?? [],
        equipmentIds: equipmentSelected ?? [],
        showStale: showStales
      });
    }

    setEquipmentSelected([]);
    setPoint([]);
    setFunctionIds([]);

    setEquipmentOptions([]);
    setPointOptions([]);
    setFunctionOptions([]);
  };

  const handleEquipmentChange = equipmentIds => {
    setEquipmentSelected(equipmentIds);

    if(equipmentIds.length > 0) {
      getDataInspectorEquipmentPoints({
        buildingIds: buildingSelected ?? [],
        equipmentIds: equipmentIds ?? [],
        showStale: showStales
      });
      getDataInspectorEquipmentFunctions({
        buildingIds: buildingSelected ?? [],
        equipmentIds: equipmentIds ?? [],
        showStale: showStales
      });
    }
    setPoint([]);
    setFunctionIds([]);

    setPointOptions([]);
    setFunctionOptions([]);
  };

  const handlePointChange = selectedItems => {
    if (selectedItems.length > 0) {
      var temp = [];
      selectedItems.forEach(function (pointId) {
        let point = pointOptions?.find(x => x.value?.includes(pointId));
        temp.push(point.value);
      });
      setPoint(temp);

      var tempEquipments = [];
      selectedItems.forEach(function (pointId) {
        tempEquipments.push(pointOptions.find(p => p.key === pointId).buildingId);
      });
      setTotalEquipmentSelected([...new Set([...totalEquipmentSelected, ...tempEquipments])]);
    } else {
      setPoint([]);
    }
  };

  const handleFunctionChange = selectedItems => {
    if (selectedItems.length > 0) {
      const temp = [];
      selectedItems.forEach(function (functionDropdownKey) {
        let tempFunction = functionOptions?.find(x => x.key === functionDropdownKey);
        temp.push({
            functionId: tempFunction.functionId,
            equipmentId: tempFunction.equipmentId,
            key: functionDropdownKey
        });
      });
      setFunctionIds(temp);


      const tempEquipments = [];
      selectedItems.forEach(function (functionDropdownKey) {
        tempEquipments.push(functionOptions.find(p => p.key === functionDropdownKey).buildingId);
      });
      setTotalEquipmentSelected([...new Set([...totalEquipmentSelected, ...tempEquipments])]);
    } else {
      setFunctionIds([]);
    }
  };

  const handleColumnChange = (value) => {
    let newcolumns = [...columns];
    newcolumns.forEach(function (column) {
      if(value.includes(column.title))
      {
        column.show = false;
      }
      else {
        column.show = true;
      }

    });
    setColumns(newcolumns);
  };


  useEffect(() => {
    setBuildingsSelected([]);
    setEquipmentSelected([]);
    setPoint([]);
    setFunctionIds([]);

    setBuildingOptions([]);
    setEquipmentOptions([]);
    setPointOptions([]);
    setFunctionOptions([]);
  }, [showStales, group]);

  useEffect(() => {
    getDataInspectorGroups();
  }, [getDataInspectorGroups]);

  useEffect(() => {
    let tempOptions = [];
    setGroupOptions([]);
    groups.forEach(group => {
      tempOptions.push({
        key: group.groupId,
        value: group.groupId,
        text: group.name,
      });
    });
    setGroupOptions(tempOptions);
  }, [groups]);

  useEffect(() => {
    let tempOptions = [];
    setBuildingOptions([]);
    buildings.forEach(equipmentItem => {
      tempOptions.push({
        key: equipmentItem.id,
        value: equipmentItem.id,
        text: `(${equipmentItem.id}) ${equipmentItem.name}`,
      });
    });
    setBuildingOptions(tempOptions);
  }, [buildings]);

  useEffect(() => {
    let tempOptions = [];
    setEquipmentOptions([]);
    equipment.forEach(equipmentItem => {
      tempOptions.push({
        key: equipmentItem.id,
        value: equipmentItem.id,
        text: `(${equipmentItem.id}) ${equipmentItem.name}`,
      });
    });
    setEquipmentOptions(tempOptions);
  }, [equipment]);

  useEffect(() => {
    if (points.length > 0 || functions.length > 0) {
      let tempOptions = [];
      setPointOptions([]);
      setFunctionOptions([]);

      //Munging point data into semeantic ui dropdown format
      let sortedPoints = [];

      points.forEach(point => {
        //skip point if it's stale
        if(!showStales && point.stale) {
            return;
        }

        let equipmentIndex = sortedPoints.findIndex(x => x.equipmentId === point.equipmentId);

        if(equipmentIndex === -1) {
          sortedPoints.push({
            equipmentName: point.equipmentName,
            equipmentId: point.equipmentId,
            points: []
          });
          equipmentIndex = sortedPoints.length - 1;
        }

        sortedPoints[equipmentIndex].points.push({
            key: point.pointId,
            value: point.pointId,
            text: `(${point.equipmentId}) ${point.pointCode}`,
            type: 'point',
            buildingId: point.equipmentId,
        });
      });

      sortedPoints.forEach(de => {
        if(de.points.length > 0) {
          tempOptions.push({
            key: de.equipmentId,
            component:
            Dropdown.Header,
            children:
              <>
                <span>
                  {`${de.equipmentName} (${de.equipmentId})`}
                </span>
              </>,
            disabled,
            className: styles.dropdownDivider,
          });

          de.points.forEach(point => {
            tempOptions.push(point);
          });
        }
      });


      setPointOptions(tempOptions);
      tempOptions = [];

      //Munging point data into semeantic ui dropdown format
      let sortedFunctions = [];
      functions.forEach(function (functionItem, index) {
        let equipmentId = functionItem.equipmentId;
        let arrIndex = sortedFunctions.findIndex(x => x.equipmentId === equipmentId);

        if(arrIndex === -1) {
          const newIndex = sortedFunctions.length;
          sortedFunctions[newIndex] = {
            equipmentName: functionItem.equipmentName,
            equipmentId: functionItem.equipmentId,
            functions: []
          };
          arrIndex = newIndex;
        }

        sortedFunctions[arrIndex].functions.push({
          key: index,
          value: index,
          functionId: functionItem.functionId,
          equipmentId: functionItem.equipmentId,
          text: `(${functionItem.equipmentId}) ${functionItem.name}`,
          type: 'function',
          buildingId: functionItem.equipmentId,
        });
      });

      sortedFunctions.forEach(de => {
        if(de.functions.length > 0) {
          tempOptions.push({
            key: de.equipmentId,
            component:
            Dropdown.Header,
            children:
                <>
                  <span>
                    {`${de.equipmentName} (${de.equipmentId})`}
                  </span>
                </>,
            disabled,
            className: styles.dropdownDivider,
          });

          de.functions.forEach(functions => {
            tempOptions.push(functions);
          });
        }
      });

      setFunctionOptions(tempOptions);
    }
  }, [points, functions, equipmentSelected, disabled, showStales]);

  useEffect(() => {
    if (group && buildingSelected && startDate && endDate && (point.length > 0 || functionIds.length > 0)) {
      setDisabled(false);
    }
  }, [group, buildingSelected, point, startDate, endDate, functionIds]);

  const handleEditClick = rowData => {
    setEditPointOpen(true);
    setEditPointData(rowData);
  };

  const handleFunctionDiagnosticsClick = rowData => {
    setFunctionDiagnosticsOpen(true);
    setFunctionDiagnosticsData(rowData);
  }

  const sort = sortState.map(s => ({ field: s.fieldName, dir: s.dir }));

  const handleSortChange = e => {
    setSortState(e.sort.length
      ? [{ fieldName: e.sort[0].field, dir: e.sort[0].dir }]
      : [{ fieldName: 'normalizedTime', dir: 'desc' }]
    );
  };

  const handlePageChange = e => {
    setPage(e.page.skip / e.page.take);
    setPageSize(e.page.take);
  };

  return (
    <div>
      <EditPointModal
        open={editPointOpen}
        setOpen={setEditPointOpen}
        pointData={editPointData}
        setPointData={setEditPointData}
      />
      <FunctionDiagnostics
        open={functionDiagnosticsOpen}
        setOpen={setFunctionDiagnosticsOpen}
        rowData={functionDiagnosticsData}
        />
      <div className={styles.pageStyle}>
        <div className="row">
          <Form>
            <Form.Group style={{flexDirection: 'column'}}>
              <div className={styles.inputRow}>
                <Dropdown
                  className={styles.inspectorDropdown}
                  defaultValue={group}
                  label="Group Id"
                  placeholder="Group Id"
                  options={groupOptions}
                  onChange={(e, {value}) => handleGroupChange(value)}
                  selection
                  search
                />
                <Dropdown
                  className={styles.inspectorDropdown}
                  label="Buildings"
                  placeholder="Buildings"
                  options={buildingOptions}
                  value={buildingSelected}
                  onChange={(e, {value}) => handleBuildingChange(value)}
                  multiple
                  selection
                  search
                  clearable
                />
                <Dropdown
                    className={styles.inspectorDropdown}
                    label="Equipment"
                    placeholder="Equipment"
                    options={equipmentOptions}
                    value={equipmentSelected}
                    onChange={(e, {value}) => handleEquipmentChange(value)}
                    multiple
                    selection
                    search
                    clearable
                />
                <Dropdown
                  className={styles.inspectorDropdown}
                  label="Points"
                  placeholder="Points"
                  options={pointOptions}
                  value={point}
                  onChange={(e, {value}) => {
                    handlePointChange(value);
                  }}
                  multiple
                  selection
                  search
                  clearable
                />
                <Dropdown
                  className={styles.inspectorDropdown}
                  label="Functions"
                  placeholder="Functions"
                  options={functionOptions}
                  value={functionIds.map(f => f.key)}
                  onChange={(e, {value}) => {
                    handleFunctionChange(value);
                  }}
                  multiple
                  selection
                  search
                  clearable
                />
              </div>
              <div className={styles.inputRow}>
                <Form.Input
                  className={styles.inspectorInput}
                  type={'date'}
                  defaultValue={startDate}
                  label="Time Range (start date)"
                  placeholder="Time Range"
                  onChange={e => setStartDate(e.target.value)}
                />
                <Form.Input
                  className={styles.inspectorInput}
                  type={'date'}
                  defaultValue={new Date().toISOString().split('T')[0]}
                  label="Time Range (end date)"
                  placeholder="Time Range"
                  onChange={e => setEndDate(e.target.value)}
                />
                <Form.Button
                  size={'large'}
                  type={'submit'}
                  color={'blue'}
                  onClick={handleSubmit}
                  disabled={disabled}
                >
                  Submit
                </Form.Button>
                <Form.Button
                  size={'large'}
                  type={'submit'}
                  color={'grey'}
                  onClick={handleExport}
                  disabled={!searched}
                >
                  {!printLoading ? 'Print' : <div className="lds-ring"></div>}
                </Form.Button>
                <div className={styles.lineToggle}>
                  <span>MultiLine</span>
                  <Form.Radio value={multiLine} toggle onChange={() => setMultiLine(!multiLine)} />
                </div>
                <div className={styles.lineToggle}>
                  <span>Show Stales</span>
                  <Form.Radio value={showStales} toggle onChange={() => setShowStales(!showStales)} />
                </div>
              </div>
            </Form.Group>
          </Form>
        </div>
        <div style={{width:'30%'}}>
          <Dropdown
              placeholder='Remove Columns'
              fluid
              multiple
              search
              selection
              options={columnsDropdown}
                onChange={(e, {value}) => handleColumnChange(value)}
          />
        </div>
        <div className={styles.toastSection}>
          <SemanticToastContainer position="bottom-center" />
        </div>
      </div>
      <div
        className={styles.tableContainer}
        style={{ display: searched ? 'block' : 'none' }}
      >
        {searched && data !== null ? (
          <div style={{ position: 'relative' }}>
            <Dimmer active={loading} inverted>
              <Loader />
            </Dimmer>
            <div style={{ overflowX: 'auto' }}>
              <Grid
                data={data ?? []}
                total={total}
                skip={page * pageSize}
                take={pageSize}
                pageable={true}
                sortable={{ allowUnsort: false }}
                sort={sort}
                onPageChange={handlePageChange}
                onSortChange={handleSortChange}
              >
                {columns[0].show ? <Column field="objectId" title="Object Id" width={110} /> : null}
                {columns[1].show ? <Column field="objectName" title="Object Name" width={160} sortable={false} cell={props => <td>{props.dataItem.objectName}</td>} /> : null}
                {columns[2].show ? <Column field="groupId" title="Group Id" width={100} /> : null}
                {columns[3].show ? <Column field="normalizedTime" title="Normalized Time" width={190} cell={props => <td>{props.dataItem.normalizedTimeFormatted}</td>} /> : null}
                {columns[4].show ? <Column field="oat" title="OAT" width={80} cell={props => <td>{props.dataItem.OAT}</td>} /> : null}
                {columns[5].show ? <Column field="dewpt" title="DEWPT" width={90} /> : null}
                {columns[6].show ? <Column field="enthalpy" title="Enthalpy" width={110} /> : null}
                {point && columns[7].show ? <Column field="point.pointCode" title="Point Code" width={130} sortable={false} cell={props => <td>{props.dataItem.point.pointCode}</td>} /> : null}
                {point && columns[8].show ? <Column field="point.pointId" title="Point Id" width={320} sortable={false} cell={props => <td>{props.dataItem.point.pointId}</td>} /> : null}
                {point && columns[9].show ? <Column field="point.componentPointId" title="Component Point Id" width={180} sortable={false} cell={props => <td>{props.dataItem.point.componentPointId}</td>} /> : null}
                {point && columns[10].show ? <Column field="point.timestamp" title="Timestamp" width={190} sortable={false} cell={props => <td>{props.dataItem.point.timestampFormatted}</td>} /> : null}
                {point && columns[11].show ? (
                  <Column field="point.value" title="Value" width={110} sortable={false} cell={props => (
                    <td>
                      <button className={styles.editButton} onClick={() => handleEditClick(props.dataItem)}>
                        {props.dataItem.point.value}
                      </button>
                    </td>
                  )} />
                ) : null}
                {point && columns[12].show ? <Column field="point.stale" title="P_Stale" width={90} sortable={false} cell={props => <td>{props.dataItem.point.stale}</td>} /> : null}
                {point && columns[13].show ? <Column field="point.down" title="P_Down" width={90} sortable={false} cell={props => <td>{props.dataItem.point.down}</td>} /> : null}
                {point && columns[14].show ? <Column field="point.fault" title="P_Fault" width={90} sortable={false} cell={props => <td>{props.dataItem.point.fault}</td>} /> : null}
                {point && columns[15].show ? <Column field="point.duplicate" title="P_Duplicate" width={110} sortable={false} cell={props => <td>{props.dataItem.point.duplicate}</td>} /> : null}
                {point && columns[16].show ? (
                  <Column field="point.error" title="P_Error" width={90} sortable={false} cell={props => (
                    <td>
                      <Popup
                        content={props.dataItem.point.errorMessage}
                        trigger={
                          <button className={styles.editButton} onClick={() => handleEditClick(props.dataItem)}>
                            {props.dataItem.point.error}
                          </button>
                        }
                      />
                    </td>
                  )} />
                ) : null}
                {functionIds && columns[17].show ? (
                  <Column field="function.functionIds" title="Function Id" width={120} sortable={false} cell={props => (
                    <td>
                      <button className={styles.editButton} onClick={() => handleFunctionDiagnosticsClick(props.dataItem)}>
                        {props.dataItem.function.functionIds}
                      </button>
                    </td>
                  )} />
                ) : null}
                {functionIds && columns[18].show ? <Column field="function.name" title="F_Name" width={160} sortable={false} cell={props => <td>{props.dataItem.function.name}</td>} /> : null}
                {functionIds && columns[19].show ? <Column field="function.value" title="F_Value" width={110} sortable={false} cell={props => <td>{props.dataItem.function.value}</td>} /> : null}
                {functionIds && columns[20].show ? (
                  <Column field="function.error" title="F_Error" width={90} sortable={false} cell={props => (
                    <td>
                      <Popup
                        content={props.dataItem.function.errorMessage}
                        trigger={<div>{props.dataItem.function.error}</div>}
                      />
                    </td>
                  )} />
                ) : null}
              </Grid>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
